import { apiEmployeeTermination } from "@/api/employees/employee-termination.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { employeeEmploymentHistoryKeys } from "@/queries/employees/use-employee-employment-history.query.ts";
import { employeesKeys } from "@/queries/employees/use-employees.query.ts";
import { ApiErrorException } from "@/types/api.types.ts";
import { ApiEmployeeTerminationPayload } from "@/types/employees/employee-termination.types.ts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

export interface SaveParams {
  employeeId: number;
  terminationId?: number;
  payload: ApiEmployeeTerminationPayload;
}

export const useEmployeeTerminationSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({ employeeId, terminationId, payload }: SaveParams) => {
      const result = !terminationId
        ? await apiEmployeeTermination.create(employeeId, payload)
        : await apiEmployeeTermination.update(employeeId, terminationId, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: employeesKeys.detail(variables.employeeId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: employeeEmploymentHistoryKeys.all(variables.employeeId).queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useEmployeeTerminationDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ employeeId, positionId }: { employeeId: number; positionId: number }) => {
      const result = await apiEmployeeTermination.delete(employeeId, positionId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: employeesKeys.detail(variables.employeeId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: employeeEmploymentHistoryKeys.all(variables.employeeId).queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
